/**
 * The claim in the headline, shown rather than asserted.
 *
 * The page says *edges that never fray* and until now never demonstrated it.
 * This is the one thing on the site no competitor can copy, because none of
 * them fix the problem — so it is worth the band it takes.
 *
 * **It is the feature, not a picture of the feature.** Both halves come from
 * the real `prepareExport`, the same call the export sheet makes; the only
 * difference between them is the `edges` setting. A screenshot of a seam would
 * be a claim about our own screenshot.
 *
 * Magnified 8×, because that is the only scale at which any of this is visible:
 * the pipeline writes into the 2px gutters between faces, which at page size is
 * well under one pixel. The export sheet learned the same lesson — see its
 * `setZoom`.
 */
import { CANVAS, type EdgeSettings, type GarmentType } from "../../shared/format";
import { REGIONS } from "../../shared/regions";
import { type PreparedExport, prepareExport } from "../app/export";

/** Everything off, so the left half is the file exactly as drawn. */
const OFF: EdgeSettings = {
  alphaHardening: "never",
  seamBleed: false,
  // Added to EdgeSettings after this file was written. It is the stage that
  // moves painted texels toward their partner across an edge, so leaving it on
  // would put half the fix on the "before" side and understate the comparison.
  seamBlend: false,
  dilationPasses: 0,
  colourBleedPasses: 0,
};

/**
 * Where to magnify.
 *
 * The top-left of the front torso, where four gutters meet — the busiest
 * junction on the template, so one crop shows several boundaries at once.
 * Computed from the measured map rather than typed as a percentage.
 */
function seamOrigin(): { x: number; y: number } {
  const r = REGIONS["torso.front"];
  return { x: (r.x / CANVAS.width) * 100, y: (r.y / CANVAS.height) * 100 };
}

export interface SeamSlider {
  el: HTMLElement;
  /** Re-run both exports for this artwork. Null clears back to the invitation. */
  show(source: HTMLCanvasElement | null, garment: GarmentType): Promise<void>;
  destroy(): void;
}

export function createSeamSlider(): SeamSlider {
  const root = document.createElement("div");
  root.className = "seam";

  const stage = document.createElement("div");
  stage.className = "seam__stage";
  const origin = seamOrigin();
  stage.style.setProperty("--seam-x", `${origin.x.toFixed(2)}%`);
  stage.style.setProperty("--seam-y", `${origin.y.toFixed(2)}%`);

  const before = document.createElement("img");
  before.className = "seam__img seam__img--before";
  before.alt = "The template exported exactly as drawn, magnified at a seam";

  // The clipped half. Its width is the handle position, so the two images stay
  // pixel-aligned rather than being two independently scaled things.
  const afterWrap = document.createElement("div");
  afterWrap.className = "seam__after";
  const after = document.createElement("img");
  after.className = "seam__img";
  after.alt = "The same seam after the edge pass, magnified";
  afterWrap.appendChild(after);

  const handle = document.createElement("input");
  handle.type = "range";
  handle.min = "0";
  handle.max = "100";
  handle.value = "50";
  handle.className = "seam__handle";
  // A real range input: it is draggable, arrow-key operable and announced,
  // none of which a div with a pointerdown listener gets for free.
  handle.setAttribute("aria-label", "Reveal the cleaned edges");

  const status = document.createElement("p");
  status.className = "seam__status";
  status.setAttribute("aria-live", "polite");

  stage.appendChild(before);
  stage.appendChild(afterWrap);
  stage.appendChild(handle);
  root.appendChild(stage);
  root.appendChild(status);

  let prepared: PreparedExport[] = [];
  let seq = 0;
  let dead = false;

  /*
   * An explicit empty state from the start.
   *
   * Without it the band renders as a blank bordered box with a paragraph above
   * telling you to drag something — and there is nothing in it to drag. The
   * band only has anything to compare once a template is loaded, so it should
   * say what it is waiting for.
   */
  root.dataset.state = "empty";
  status.textContent = "Load a template above to compare the two exports.";
  handle.disabled = true;

  function setSplit(pct: number) {
    // Clipping the overlay rather than resizing it: a resized image would
    // rescale its contents and the two halves would stop lining up.
    afterWrap.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
    stage.style.setProperty("--split", `${pct}%`);
  }

  handle.addEventListener("input", () => setSplit(Number(handle.value)));
  setSplit(50);

  function release() {
    for (const p of prepared) p.dispose();
    prepared = [];
  }

  async function show(source: HTMLCanvasElement | null, garment: GarmentType) {
    release();
    if (!source) {
      root.dataset.state = "empty";
      status.textContent = "Load a template above to compare the two exports.";
      handle.disabled = true;
      before.removeAttribute("src");
      after.removeAttribute("src");
      return;
    }

    const mine = ++seq;
    root.dataset.state = "loading";
    status.textContent = "Running the export…";

    try {
      /*
       * Two real exports of the same artwork. `body` is passed so the seam
       * correction stage actually runs — omitted, stage 2 is skipped and the
       * two halves would differ only by dilation.
       */
      const [raw, fixed] = await Promise.all([
        prepareExport(source, { title: "seam-off", garment, edges: OFF }),
        prepareExport(source, { title: "seam-on", garment, body: "r6-classic" }),
      ]);
      if (mine !== seq || dead) {
        raw.dispose();
        fixed.dispose();
        return;
      }
      prepared = [raw, fixed];
      before.src = raw.preview;
      after.src = fixed.preview;
      root.dataset.state = "ready";
      handle.disabled = false;

      const { bledPixels, haloPixels } = fixed.preflight;
      /*
       * The numbers were removed from the export sheet as noise on a
       * confirmation screen. Here they are the point: this band exists to show
       * that something happened, and "nothing needed fixing" is a real and
       * useful outcome to be able to say.
       */
      status.textContent =
        bledPixels + haloPixels === 0
          ? "Every edge on this design already sat clean."
          : `${(bledPixels + haloPixels).toLocaleString()} pixels repaired along the seams.`;
    } catch {
      root.dataset.state = "empty";
      handle.disabled = true;
      status.textContent = "Could not run the export for this design.";
    }
  }

  return {
    el: root,
    show,
    destroy() {
      dead = true;
      release();
      root.remove();
    },
  };
}
